import type { Resolver } from 'react-hook-form'
import { useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { endOfMonth, format, startOfMonth } from 'date-fns'
import { Modal, Button, DateInput, Select, Spinner, TimeInput } from '@/components/ui'
import type { SelectOption } from '@/components/ui'
import type { Company } from '@/types/company.types'
import { useMyAttendance } from '@/features/attendance/hooks/useMyAttendance'
import { useCreateTimeCorrectionRequest } from '@/features/attendance/hooks/useTimeCorrection'
import { formatDateTime } from '@/lib/formatters'
import { cn } from '@/lib/cn'
import {
  buildTimeCorrectionRequestDto,
  REASON_CODES,
  timeCorrectionRequestFormSchema,
  type TimeCorrectionRequestFormData,
} from '@/features/attendance/schemas/timeCorrection.schema'

interface CreateWorkspaceTimeCorrectionModalProps {
  open: boolean
  companies: Company[]
  defaultCompanyId: string | null
  onClose: () => void
  onSuccess?: () => void
}

const defaultValues: TimeCorrectionRequestFormData = {
  reason: '',
  requestedClockInDate: '',
  requestedClockInTime: '',
  requestedClockOutDate: '',
  requestedClockOutTime: '',
  reasonCode: '',
}

const REASON_CODE_OPTIONS = [
  { value: '', label: 'Select reason (optional)' },
  ...REASON_CODES.map((reason) => ({ value: reason.value, label: reason.label })),
]

export function CreateWorkspaceTimeCorrectionModal({
  open,
  companies,
  defaultCompanyId,
  onClose,
  onSuccess,
}: CreateWorkspaceTimeCorrectionModalProps): React.ReactElement | null {
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('')
  const [attendanceId, setAttendanceId] = useState<string>('')
  const createRequest = useCreateTimeCorrectionRequest()
  const effectiveCompanyId = selectedCompanyId || defaultCompanyId || companies[0]?._id || ''

  const companyOptions: SelectOption[] = companies.map((company) => ({
    value: company._id,
    label: company.name,
  }))

  const now = new Date()
  const attendanceQuery = useMyAttendance(
    effectiveCompanyId
      ? {
          companyIds: [effectiveCompanyId],
          startDate: format(startOfMonth(now), 'yyyy-MM-dd'),
          endDate: format(endOfMonth(now), 'yyyy-MM-dd'),
          page: 1,
          limit: 100,
        }
      : undefined,
    { enabled: open && !!effectiveCompanyId }
  )

  const attendanceOptions: SelectOption[] = useMemo(
    () =>
      (attendanceQuery.data?.data ?? []).map((attendance) => ({
        value: attendance._id,
        label: `${attendance.date?.slice(0, 10) ?? 'Attendance'} · ${
          attendance.clockIn ? formatDateTime(attendance.clockIn) : 'No clock-in'
        }`,
      })),
    [attendanceQuery.data?.data]
  )

  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TimeCorrectionRequestFormData>({
    resolver: zodResolver(
      timeCorrectionRequestFormSchema
    ) as Resolver<TimeCorrectionRequestFormData>,
    defaultValues,
  })
  const reasonCode = useWatch({ control, name: 'reasonCode' })
  const requestedClockInTime = useWatch({ control, name: 'requestedClockInTime' })
  const requestedClockOutTime = useWatch({ control, name: 'requestedClockOutTime' })

  const handleClose = (): void => {
    reset(defaultValues)
    setAttendanceId('')
    onClose()
  }

  const onSubmit = (data: TimeCorrectionRequestFormData): void => {
    if (!effectiveCompanyId || !attendanceId) return
    const body = buildTimeCorrectionRequestDto(data, attendanceId)
    createRequest.mutate(
      { companyId: effectiveCompanyId, body },
      {
        onSuccess: () => {
          reset(defaultValues)
          setAttendanceId('')
          onClose()
          onSuccess?.()
        },
      }
    )
  }

  if (!open) return null

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Request time correction"
      className="max-w-xl"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="workspace-time-correction-form"
            disabled={!effectiveCompanyId || !attendanceId} loading={isSubmitting || createRequest.isPending}
          >
            Submit request
          </Button>
        </>
      }
    >
      <form
        id="workspace-time-correction-form"
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-5"
      >
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Company
          </label>
          <Select
            options={companyOptions}
            value={effectiveCompanyId}
            onChange={(value) => {
              setSelectedCompanyId(value ?? '')
              setAttendanceId('')
            }}
            placeholder="Select company..."
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Attendance record <span className="text-danger">*</span>
          </label>
          <Select
            options={attendanceOptions}
            value={attendanceId}
            onChange={(value) => setAttendanceId(value ?? '')}
            placeholder={
              attendanceQuery.isLoading ? 'Loading attendance...' : 'Select attendance...'
            }
            searchable
          />
          {attendanceQuery.isLoading && (
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <Spinner size="sm" className="h-3.5 w-3.5" aria-hidden />
              Loading attendance records
            </div>
          )}
          {!attendanceQuery.isLoading && effectiveCompanyId && attendanceOptions.length === 0 && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              No attendance records found for this month.
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="workspace-time-correction-reason"
            className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Reason <span className="text-danger">*</span>
          </label>
          <textarea
            id="workspace-time-correction-reason"
            {...register('reason')}
            rows={3}
            className={cn(
              'block w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500',
              errors.reason
                ? 'border-danger focus:ring-danger'
                : 'border-gray-300 dark:border-gray-600'
            )}
            placeholder="e.g. Forgot to clock out"
            aria-invalid={!!errors.reason}
          />
          {errors.reason && <p className="mt-1 text-sm text-danger">{errors.reason.message}</p>}
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Reason code (optional)
          </label>
          <Select
            options={REASON_CODE_OPTIONS}
            value={reasonCode ?? ''}
            onChange={(value) => setValue('reasonCode', value ?? '', { shouldValidate: true })}
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-3.5 dark:border-gray-700 dark:bg-gray-900/30">
          <div className="mb-3 hidden gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:grid sm:grid-cols-[7.5rem_minmax(0,1fr)_9rem]">
            <span className="hidden sm:block" />
            <span>Date</span>
            <span>Time</span>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-[7.5rem_minmax(0,1fr)_9rem] sm:items-center">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Clock-in</span>
              <DateInput {...register('requestedClockInDate')} aria-label="Clock-in date" />
              <TimeInput
                value={requestedClockInTime ?? ''}
                onChange={(value) =>
                  setValue('requestedClockInTime', value, { shouldValidate: true })
                }
              />
            </div>

            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-[7.5rem_minmax(0,1fr)_9rem] sm:items-center">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Clock-out
              </span>
              <DateInput {...register('requestedClockOutDate')} aria-label="Clock-out date" />
              <TimeInput
                value={requestedClockOutTime ?? ''}
                onChange={(value) =>
                  setValue('requestedClockOutTime', value, { shouldValidate: true })
                }
              />
            </div>
          </div>
        </div>

        {(errors.requestedClockInDate ?? errors.requestedClockOutDate) && (
          <p className="text-sm text-danger">
            {errors.requestedClockInDate?.message ?? errors.requestedClockOutDate?.message}
          </p>
        )}
      </form>
    </Modal>
  )
}

CreateWorkspaceTimeCorrectionModal.displayName = 'CreateWorkspaceTimeCorrectionModal'
